import { IconResize } from '@consta/icons/IconResize'
import { PortalWithTheme } from '@consta/uikit/PortalWithTheme'
import { useTheme } from '@consta/uikit/Theme'
import type { WindowLimitsType, WindowOffsetsType } from '@library/types'
import classNames from 'classnames/bind'
import type { Nullable } from 'common/types'
import type { CSSProperties, FC, PropsWithChildren } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { usePinnedResize } from '../../hooks'
import { RESIZE_ICON_PROPS } from './constants'
import css from './ResizableModal.css'

const cn = classNames.bind(css)

type ResizablePinnedModalProps = {
  className?: string
  isOpen: boolean
  isPinnedAtBottomRight: boolean
  anchor?: HTMLElement
  parentElement?: Nullable<HTMLElement>
  limits?: WindowLimitsType
  offsets?: WindowOffsetsType
}

export const ResizablePinnedModal: FC<PropsWithChildren<ResizablePinnedModalProps>> = ({
  className = '',
  children,
  anchor,
  parentElement,
  limits,
  offsets,
  isOpen,
  isPinnedAtBottomRight,
}) => {
  const [modal, setModal] = useState<HTMLDivElement>()
  const modalFn = useCallback((node: HTMLDivElement) => {
    setModal(node)
  }, [])
  const iconFn = useRef<Nullable<HTMLDivElement>>(null)
  const visibility: CSSProperties['visibility'] = isOpen ? 'visible' : 'hidden'

  const iconResizeClassName = cn(css.iconResize, {
    [css.iconResizeBottom]: !isPinnedAtBottomRight,
    [css.iconResizeTop]: isPinnedAtBottomRight,
  })

  useEffect(() => {
    if (anchor === undefined) {
      return
    }

    anchor.style.userSelect = 'none'
    anchor.style.cursor = 'move'
  }, [anchor])

  const { theme } = useTheme()
  usePinnedResize(
    isPinnedAtBottomRight,
    modal,
    iconFn.current ?? undefined,
    parentElement ?? undefined,
    limits,
    offsets,
  )

  return (
    <PortalWithTheme preset={theme}>
      <div ref={modalFn} className={`${css.container} ${className}`} style={{ visibility }}>
        {isPinnedAtBottomRight ? (
          <IconResize ref={iconFn} className={iconResizeClassName} {...RESIZE_ICON_PROPS} />
        ) : (
          children
        )}
        {!isPinnedAtBottomRight ? (
          <IconResize ref={iconFn} className={iconResizeClassName} {...RESIZE_ICON_PROPS} />
        ) : (
          children
        )}
      </div>
    </PortalWithTheme>
  )
}
